import { YStack } from '@mythical/ui'
import { Button } from 'app/components/primitive/Button'
import {
  IconChevronLeft,
  IconChevronRight,
} from 'app/components/primitive/Icon'
import { PaginationDots } from '../PaginationDots'
import { ZoomableImage } from '../ZoomableImage/index.web'
import { ContentCarousel } from 'app/components/content/ContentCarousel'
import { useCarouselController } from 'app/hooks/use-carousel-controller'

export type ImageCarouselProps = {
  images: {
    image: string
    thumbnail: string
    alt: string
  }[]
}

const ImageCarousel = ({ images }: ImageCarouselProps) => {
  const { carouselRef, ...controller } = useCarouselController()

  return (
    <YStack gap="$3.5" $gtSm={{ overflow: 'hidden' }}>
      <ContentCarousel
        items={images}
        keyExtractor={(item) => item.image}
        sizeConfig={{
          width: '100%',
          spacing: 8,
          $gtXs: {
            width: 450,
            spacing: 16,
          },
          $gtSm: {
            width: 450,
            spacing: 16,
          },
        }}
        renderItem={(item, i) => (
          <ZoomableImage
            src={item.thumbnail}
            srcZoomed={item.image}
            alt={item.alt}
            sizes="(min-width: 660px) 350px, 100vw"
          />
        )}
        controllerRef={carouselRef}
      />

      <YStack
        zIndex={1}
        position="absolute"
        l="$2"
        h="100%"
        opacity={controller.canScrollPrevious ? 1 : 0}
        x={controller.canScrollPrevious ? 0 : -100}
        animation="quick"
        $xs={{
          display: 'none',
        }}
      >
        <YStack height="100%" ai="center" jc="center">
          <Button
            theme="secondary"
            circular
            icon={<IconChevronLeft size={32} color="$fgPrimary" />}
            onPress={controller.scrollToPrevious}
            aria-label="Previous"
          />
        </YStack>
      </YStack>

      <YStack
        zIndex={1}
        position="absolute"
        r="$2"
        h="100%"
        x={controller.canScrollNext ? 0 : 100}
        opacity={controller.canScrollNext ? 1 : 0}
        animation="quick"
        $xs={{
          display: 'none',
        }}
      >
        <YStack height="100%" ai="center" jc="center">
          <Button
            theme="secondary"
            circular
            icon={<IconChevronRight size={32} color="$fgPrimary" />}
            onPress={controller.scrollToNext}
            aria-label="Next"
          />
        </YStack>
      </YStack>

      <PaginationDots
        count={images.length}
        activeIndex={controller.currentIndex}
        onChange={(index) => controller.scrollToIndex(index)}
        $gtXs={{
          display: 'none',
        }}
      />
    </YStack>
  )
}

export default ImageCarousel
